import React, { useState } from 'react';
import { Box, Typography, Stack, Button, Paper, Grid, IconButton, Card, CardContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../../UserContext';

export default function PartDetails({ part, onResetSearch, onBack, onAddToInvoice, invoiceMode }) {
  console.log('part in PartDetails', part);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const images = [part['IMAGE1'], part['IMAGE2']].filter(Boolean);
  const navigate = useNavigate();
  const theme = useTheme();
  const { userRecord } = useUser();
  
  const handleResetSearch = () => {
    onResetSearch();
  }

  const calculateAdjustedPrice = (originalPrice, user) => {
    if (!user || !user.priceOffsetType || !user.priceOffsetValue) {
      console.log("No price offset applied, returning original price:", originalPrice);
      return originalPrice;
    }

    const price = parseFloat(originalPrice);
    if (isNaN(price)) {
      console.log("Original price is not a number, returning as is:", originalPrice);
      return originalPrice;
    }

    if (user.priceOffsetType === 'percentage') {
      // Apply percentage offset
      const percentageOffset = parseFloat(user.priceOffsetValue) / 100;
      const adjusted = price * (1 + percentageOffset);
      console.log(`Applied percentage offset: ${percentageOffset * 100}%, original: ${price}, adjusted: ${adjusted}`);
      return adjusted;
    } else if (user.priceOffsetType === 'fixed') {
      // Apply fixed dollar amount offset
      const fixedOffset = parseFloat(user.priceOffsetValue);
      const adjusted = price + fixedOffset;
      console.log(`Applied fixed offset: $${fixedOffset}, original: ${price}, adjusted: ${adjusted}`);
      return adjusted;
    }

    console.log("Unknown offset type, returning original price:", originalPrice);
    return originalPrice;
  };

  // Format price to round to the nearest dollar
  const formatPrice = (price) => {
    return Math.round(parseFloat(price));
  };

  // Determine the final price to display
  // Priority: groupAdjustedPrice (from group pricing) > finalPrice > adjustedPrice > PRICE
  let finalPrice;
  
  if (part.groupAdjustedPrice !== undefined) {
    // Use group-adjusted price if available (group pricing overrides individual offsets)
    finalPrice = part.groupAdjustedPrice;
    console.log("PartDetails - Using group-adjusted price:", finalPrice);
  } else if (part.finalPrice !== undefined) {
    // Use final price if available (already calculated with adjustments)
    finalPrice = part.finalPrice;
    console.log("PartDetails - Using pre-calculated final price:", finalPrice);
  } else {
    // Fall back to calculating price with individual user adjustments
    const priceToUse = part.adjustedPrice ? part.adjustedPrice : part['PRICE'];
    console.log("PartDetails - Price being used as base:", priceToUse);
    console.log("PartDetails - Original PRICE:", part['PRICE']);
    console.log("PartDetails - Pricelist adjusted price:", part.adjustedPrice);
    
    finalPrice = calculateAdjustedPrice(priceToUse, userRecord);
    console.log("PartDetails - Final price after user adjustments:", finalPrice);
  }

  // Get the price to display
  const displayPrice = finalPrice;

  return (
    <Box 
      sx={{ 
        backgroundColor: '#000000',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: 4,
        pb: 4
      }}
    >
      <Box sx={{ maxWidth: '1200px', width: '100%', px: 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          mb: 4,
          position: 'relative'
        }}>
          <IconButton 
            onClick={onResetSearch}
            sx={{ 
              position: 'absolute',
              left: 0,
              color: theme.palette.secondary.main,
              border: `1px solid ${theme.palette.secondary.main}`,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              textAlign: 'center'
            }}
          >
            Part Details
          </Typography>
        </Box>

        {/* Main Content Card */}
        <Card 
          sx={{ 
            backgroundColor: '#000000',
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: '16px',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4} alignItems="center">
              
              {/* Left Side - Images */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  
                  {/* Main Image */}
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px',
                      width: '100%',
                      maxWidth: '500px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '400px',
                      border: `1px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    {images.length > 0 ? (
                      <img
                        src={`https://s3.us-east-2.amazonaws.com/366images/${images[activeImageIndex]}`}
                        alt={part['PART NUMBER']}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '360px',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#666666',
                          textAlign: 'center'
                        }}
                      >
                        No Image Available
                      </Typography>
                    )}
                  </Box>

                  {/* Image Navigation - only show if multiple images */}
                  {images.length > 1 && (
                    <>
                      {/* Navigation Dots */}
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        justifyContent="center"
                        sx={{ marginBottom: '20px' }}
                      >
                        {images.map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: activeImageIndex === index ? theme.palette.primary.main : theme.palette.text.secondary,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                              }
                            }}
                            onClick={() => setActiveImageIndex(index)}
                          />
                        ))}
                      </Stack>

                      {/* Thumbnail Images */}
                      <Stack 
                        direction="row" 
                        spacing={2} 
                        justifyContent="center"
                        sx={{ flexWrap: 'wrap', gap: 2 }}
                      >
                        {images.map((image, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: '80px',
                              height: '80px',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              padding: '8px',
                              cursor: 'pointer',
                              border: activeImageIndex === index 
                                ? `2px solid ${theme.palette.primary.main}` 
                                : `1px solid ${theme.palette.text.secondary}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                              }
                            }}
                            onClick={() => setActiveImageIndex(index)}
                          >
                            <img
                              src={`https://s3.us-east-2.amazonaws.com/366images/${image}`}
                              alt={`${part['PART NUMBER']}-${index}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Right Side - Part Information */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  justifyContent: 'center'
                }}>
                  
                  {/* Part Number */}
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      mb: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    {part['PART NUMBER']}
                  </Typography>

                  {/* Description */}
                  {part['DESCRIPTION'] && (
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        mb: 4,
                        maxWidth: '400px',
                        lineHeight: 1.5
                      }}
                    >
                      {part['DESCRIPTION']}
                    </Typography>
                  )}

                  {/* Price */}
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '12px',
                      padding: '20px 40px',
                      mb: 4,
                      border: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        color: '#000000',
                        fontWeight: 'bold'
                      }}
                    >
                      ${formatPrice(displayPrice)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                      startIcon={<SearchIcon />}
                      variant="outlined"
                      onClick={handleResetSearch}
                      sx={{
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.main,
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '1rem',
                        '&:hover': {
                          borderColor: theme.palette.secondary.main,
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      New Search
                    </Button>
                    
                    {invoiceMode && onAddToInvoice && (
                      <Button
                        variant="contained"
                        onClick={() => onAddToInvoice(part)}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: '#000000',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          fontSize: '1rem',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        Add to Invoice
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
} 